// @flow

import React, { type Node, useCallback } from 'react';
import styled from 'styled-components';
import type { PaymentMethodCode } from '@tectonics/prs/payment-method';
import { useUniqueId } from '@tectonics/hooks';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useUserInputValue } from '@tectonics/prs/user-input-values';

import { RadioButton, Separator } from '../../../../../../commons/atoms';
import { DisplayBrands } from './display-brands';
import { FieldsHolder } from '../../commons';

type Props = {|
  paymentMethodCode: PaymentMethodCode,
  title: Node,
  children: Node,
  opened: boolean,
|};

export function SelectablePaymentMethodBox({ paymentMethodCode, title, children, opened }: Props): Node {
  const id = useUniqueId();
  const [
    selectedPaymentMethod,
    setSelectEbanxPaymentMethod,
  ] = useUserInputValue('selectedPaymentMethod');
  const isChecked = paymentMethodCode === selectedPaymentMethod;
  const submitting = useSubmitting();
  const handleSelectPaymentMethod = useCallback((event) => {
    setSelectEbanxPaymentMethod(event.target.value);
  }, [setSelectEbanxPaymentMethod]);

  return (
    <Root opened={opened}>

      <Header
        htmlFor={id}
        disabled={submitting}
      >

        <RadioButton
          name="payment-method"
          id={id}
          checked={isChecked}
          onChange={handleSelectPaymentMethod}
          value={paymentMethodCode}
          data-test-value={paymentMethodCode}
        />

        <Separator direction="horizontal" size={3} />

        <Title>
          {title}
        </Title>

        <DisplayBrandsWrapper>
          <DisplayBrands paymentMethodCode={paymentMethodCode}/>
        </DisplayBrandsWrapper>

      </Header>

      <ChildrenWrapper>
        {children}
      </ChildrenWrapper>

    </Root>
  );
}

const Header = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  padding: 15px 22px;
  cursor: pointer;
`;

const ChildrenWrapper = styled(FieldsHolder)`
  border-top: 1px solid #dddddd;
  &:empty {
    border: none;
  }
`;

const Root = styled.div`
  background: rgb(250, 250, 250);
  border: 1px solid #dddddd;
  border-bottom-width: 0;

  &:first-child {
    &, ${Header} {
      border-top-right-radius: 5px;
      border-top-left-radius: 5px;
    }
  }

  &:last-child {
    &, ${Header} {
      border-bottom-width: 1px;
      border-bottom-right-radius: 5px;
      border-bottom-left-radius: 5px;
    }
  }
`;

const Title = styled.span`
  flex-grow: 1;
  color: #5c5c5c;
`;

const DisplayBrandsWrapper = styled.div`
  max-width: 50%;
`;
